import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useState, type ReactElement, type ReactNode } from "react";
import { ContentHeader, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import {
  SANDBOX_TRACKING_EVENTS,
  type FileLink,
  type ShipmentAddress,
  type ShipmentView,
} from "@cx/contracts";
import { formatBRL, formatDateBR, formatQty } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { dateTimeBR } from "../invoices/InvoiceEvents";
import { CancelShipmentDialog, ReverseDialog } from "./LogisticsDialogs";
import {
  SHIPMENT_STATUS_TONE,
  cepLabel,
  daysLabel,
  dimsLabel,
  kgLabel,
  providerLabel,
  statusLabel,
  trackingUrl,
} from "./labels";

const Info = ({ label, children }: { label: string; children: ReactNode }) => (
  <>
    <dt className="col-sm-4">{label}</dt>
    <dd className="col-sm-8">{children}</dd>
  </>
);

const Address = ({ a }: { a: ShipmentAddress }) => (
  <address className="small mb-0">
    <strong>{a.name}</strong>
    {a.document && <span className="text-muted"> · {a.document}</span>}
    <br />
    {a.street}, {a.number}
    {a.complement ? ` ${a.complement}` : ""} — {a.district}
    <br />
    {a.city}/{a.uf} · CEP {cepLabel(a.cep)}
    {(a.email || a.phone) && (
      <>
        <br />
        <span className="text-muted">{[a.email, a.phone].filter(Boolean).join(" · ")}</span>
      </>
    )}
  </address>
);

const SANDBOX_EVENT_LABEL: Record<(typeof SANDBOX_TRACKING_EVENTS)[number], string> = {
  posted: "Postado",
  in_transit: "Em trânsito",
  out_for_delivery: "Saiu para entrega",
  delivered: "Entregue",
  failed: "Tentativa sem sucesso",
  returned: "Devolvido",
};

type Dialog = "cancel" | "reverse" | null;

/**
 * Objeto de postagem (T3.4): etiqueta, rastreio (eventos), remetente/destinatário, conteúdo, cancelar, tentar de novo,
 * logística reversa. No ambiente de testes, simula eventos de rastreio no provedor falso.
 */
export function ShipmentPage(): ReactElement {
  const { id } = useParams({ strict: false }) as { id: string };
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const [dialog, setDialog] = useState<Dialog>(null);
  const key = ["logistics", companyId, "shipment", id];
  const query = useQuery({
    queryKey: key,
    queryFn: () => api<ShipmentView>(`/logistics/shipments/${id}`),
    // Etiqueta e cancelamento saem pela fila: acompanha até sair de "pendente"/"cancelando".
    refetchInterval: (q) =>
      q.state.data && ["pending", "canceling"].includes(q.state.data.status) ? 2000 : false,
  });
  const done = (s: ShipmentView) => {
    setDialog(null);
    if (s.id === id) qc.setQueryData(key, s);
    void qc.invalidateQueries({ queryKey: ["logistics", companyId] });
  };
  const onError = (e: Error) => toast("danger", errorMessage(e));
  const label = useMutation({
    mutationFn: () => api<FileLink>(`/logistics/shipments/${id}/label`),
    onSuccess: (r) => window.open(r.url, "_blank", "noopener"),
    onError,
  });
  const retry = useMutation({
    mutationFn: () => api<ShipmentView>(`/logistics/shipments/${id}/retry`, { method: "POST", body: "{}" }),
    onSuccess: (s) => {
      toast("success", "Etiqueta enviada de novo ao provedor");
      done(s);
    },
    onError,
  });
  const track = useMutation({
    mutationFn: () =>
      api<{ queued: number }>("/logistics/shipments/track", {
        method: "POST",
        body: JSON.stringify({ ids: [id] }),
      }),
    onSuccess: () => {
      toast("success", "Rastreio solicitado");
      setTimeout(() => void qc.invalidateQueries({ queryKey: key }), 2500);
    },
    onError,
  });
  const sandboxEvent = useMutation({
    mutationFn: (event: string) =>
      api<{ ok: true }>(`/logistics/shipments/${id}/sandbox-event`, {
        method: "POST",
        body: JSON.stringify({ event }),
      }),
    onSuccess: () => {
      toast("success", "Evento simulado. O rastreio se atualiza em instantes.");
      setTimeout(() => void qc.invalidateQueries({ queryKey: key }), 2500);
    },
    onError,
  });
  const s = query.data;
  const canUpdate = can(perms, "logistics", "update");
  const canCreate = can(perms, "logistics", "create");
  const url = trackingUrl(s?.trackingCode ?? null);
  return (
    <>
      <ContentHeader
        title={s ? `${s.isReverse ? "Logística reversa" : "Objeto"} ${s.number}` : "Objeto de postagem"}
        breadcrumb={["Vendas", "Logística", s ? String(s.number) : ""]}
        actions={
          s && (
            <>
              <Link to="/vendas/logistica" className="btn btn-outline-secondary">
                Voltar
              </Link>
              {s.can.label && (
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  disabled={label.isPending}
                  onClick={() => label.mutate()}
                >
                  {s.isReverse ? "Imprimir autorização" : "Imprimir etiqueta"}
                </button>
              )}
              {canUpdate && s.can.track && (
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  disabled={track.isPending}
                  onClick={() => track.mutate()}
                >
                  Atualizar rastreio
                </button>
              )}
              {canUpdate && s.can.retry && (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={retry.isPending}
                  onClick={() => retry.mutate()}
                >
                  Tentar de novo
                </button>
              )}
              {canCreate && s.can.reverse && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setDialog("reverse")}
                >
                  Logística reversa
                </button>
              )}
              {canUpdate && s.can.cancel && (
                <button type="button" className="btn btn-outline-danger" onClick={() => setDialog("cancel")}>
                  Cancelar
                </button>
              )}
            </>
          )
        }
      />
      <QueryStatus loading={query.isLoading} error={query.error} />
      {s && (
        <div className="row">
          <div className="col-xl-8">
            {s.status === "error" && s.errorMessage && (
              <div className="alert alert-danger" role="alert">
                {s.errorMessage}
              </div>
            )}
            {s.status === "pending" && (
              <div className="alert alert-info py-2">
                {s.isReverse ? "Emitindo a autorização de postagem…" : "Gerando a etiqueta no provedor…"}
              </div>
            )}
            <div className="box card p-3 mb-3">
              <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                <span data-testid="shipment-status">
                  <StatusBadge tone={SHIPMENT_STATUS_TONE[s.status]}>
                    {statusLabel(s.status, s.isReverse)}
                  </StatusBadge>
                </span>
                <span>
                  Rastreio{" "}
                  <strong className="font-monospace" data-testid="shipment-tracking">
                    {s.trackingCode ?? "—"}
                  </strong>
                </span>
                {url && (
                  <a href={url} target="_blank" rel="noreferrer" className="small">
                    Rastrear nos Correios
                  </a>
                )}
              </div>
              <dl className="row mb-0">
                <Info label="Serviço">
                  {s.carrierName ? `${s.carrierName} ` : ""}
                  {s.serviceName} · {s.integrationName} ({providerLabel(s.provider)})
                </Info>
                <Info label="Pedido">
                  {s.salesOrderId ? (
                    <Link to="/vendas/pedidos/$id" params={{ id: s.salesOrderId }}>
                      {s.salesOrderNumber}
                    </Link>
                  ) : (
                    "—"
                  )}
                </Info>
                {s.invoiceKey && (
                  <Info label="NF-e">
                    {s.fiscalDocumentId ? (
                      <Link to="/vendas/notas-fiscais/$id" params={{ id: s.fiscalDocumentId }}>
                        <span className="small text-break">{s.invoiceKey}</span>
                      </Link>
                    ) : (
                      <span className="small text-break">{s.invoiceKey}</span>
                    )}
                  </Info>
                )}
                {s.reverseOfId && (
                  <Info label="Envio original">
                    <Link to="/vendas/logistica/$id" params={{ id: s.reverseOfId }}>
                      Objeto {s.reverseOfNumber}
                    </Link>
                  </Info>
                )}
                {s.reverses.length > 0 && (
                  <Info label="Reversas">
                    {s.reverses.map((r, i) => (
                      <span key={r.id}>
                        {i > 0 && ", "}
                        <Link to="/vendas/logistica/$id" params={{ id: r.id }}>
                          Reversa {r.number}
                        </Link>{" "}
                        ({statusLabel(r.status, true)})
                      </span>
                    ))}
                  </Info>
                )}
                <Info label="Pacote">
                  {kgLabel(s.weightG)} · {dimsLabel(s)}
                </Info>
                <Info label="Valor declarado">{formatBRL(s.declaredValueCents)}</Info>
                <Info label="Frete">
                  {s.freightCostCents === null ? "—" : formatBRL(s.freightCostCents)}
                  {s.quotedCents !== null && s.quotedCents !== s.freightCostCents && (
                    <span className="text-muted small"> (cotado {formatBRL(s.quotedCents)})</span>
                  )}
                  {s.deliveryDays !== null && (
                    <span className="text-muted small"> · {daysLabel(s.deliveryDays)}</span>
                  )}
                </Info>
                {s.batchId && (
                  <Info label="Remessa">
                    <Link to="/vendas/logistica/remessas/$id" params={{ id: s.batchId }}>
                      {s.batchNumber}
                    </Link>
                  </Info>
                )}
                {s.reverseExpiresAt && (
                  <Info label="Autorização válida até">{formatDateBR(s.reverseExpiresAt)}</Info>
                )}
                {s.isReverse && (
                  <Info label="Aviso ao cliente">
                    {s.notifiedAt
                      ? `E-mail enviado em ${dateTimeBR(s.notifiedAt)}`
                      : s.notifyCustomer
                        ? "Pendente"
                        : "Não enviar"}
                  </Info>
                )}
                {s.postedAt && <Info label="Postado em">{dateTimeBR(s.postedAt)}</Info>}
                {s.deliveredAt && <Info label="Entregue em">{dateTimeBR(s.deliveredAt)}</Info>}
                {s.canceledAt && (
                  <Info label="Cancelado em">
                    {dateTimeBR(s.canceledAt)} — {s.cancelReason}
                  </Info>
                )}
                {s.notes && <Info label="Observações">{s.notes}</Info>}
              </dl>
            </div>
            <div className="box card p-3 mb-3">
              <h2 className="h6">Rastreamento</h2>
              {s.events.length === 0 ? (
                <p className="text-muted small mb-0">Nenhum evento ainda.</p>
              ) : (
                <ol className="list-unstyled mb-0" aria-label="Eventos de rastreio">
                  {s.events.map((e) => (
                    <li key={e.id} className="border-start ps-3 pb-2">
                      <div className="small text-muted">{dateTimeBR(e.occurredAt)}</div>
                      <div>
                        {e.description}
                        {e.status && (
                          <StatusBadge tone={SHIPMENT_STATUS_TONE[e.status]}>
                            {statusLabel(e.status, s.isReverse)}
                          </StatusBadge>
                        )}
                      </div>
                      {e.location && <div className="small text-muted">{e.location}</div>}
                    </li>
                  ))}
                </ol>
              )}
              {s.trackedAt && (
                <p className="small text-muted mt-2 mb-0">Consultado em {dateTimeBR(s.trackedAt)}</p>
              )}
            </div>
            <div className="box card p-3 mb-3">
              <h2 className="h6">Conteúdo</h2>
              <div className="table-responsive">
                <table className="table table-sm mb-0" aria-label="Conteúdo do objeto">
                  <thead>
                    <tr>
                      <th scope="col">Item</th>
                      <th scope="col" className="text-end">
                        Qtd.
                      </th>
                      <th scope="col" className="text-end">
                        Unitário
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.contents.map((c, i) => (
                      <tr key={i}>
                        <td>
                          {c.description}
                          {c.code && <span className="text-muted small"> · {c.code}</span>}
                        </td>
                        <td className="text-end">{formatQty(c.qtyQ4)}</td>
                        <td className="text-end">{formatBRL(c.unitCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <aside className="col-xl-4">
            <div className="box card p-3 mb-3">
              <h2 className="h6">Remetente</h2>
              <Address a={s.sender} />
            </div>
            <div className="box card p-3 mb-3">
              <h2 className="h6">Destinatário</h2>
              <Address a={s.recipient} />
            </div>
            {canUpdate && s.can.track && s.provider && s.externalId && (
              <SandboxEvents
                shipment={s}
                pending={sandboxEvent.isPending}
                onEvent={(e) => sandboxEvent.mutate(e)}
              />
            )}
          </aside>
        </div>
      )}
      {s && (
        <>
          <CancelShipmentDialog
            open={dialog === "cancel"}
            shipment={s}
            onClose={() => setDialog(null)}
            onDone={done}
          />
          <ReverseDialog
            open={dialog === "reverse"}
            shipment={s}
            onClose={() => setDialog(null)}
            onDone={(r) => {
              done(r);
              void qc.invalidateQueries({ queryKey: key });
            }}
          />
        </>
      )}
    </>
  );
}

/** Só no ambiente de testes (integração sandbox): simula o provedor mandando um evento de rastreio. */
function SandboxEvents({
  shipment,
  pending,
  onEvent,
}: {
  shipment: ShipmentView;
  pending: boolean;
  onEvent(e: string): void;
}): ReactElement | null {
  const { companyId, api } = useSession();
  const overview = useQuery({
    queryKey: ["logistics", companyId, "overview"],
    queryFn: () => api<{ integrations: { id: string; sandbox: boolean }[] }>("/logistics/overview"),
  });
  const sandbox = overview.data?.integrations.find((i) => i.id === shipment.integrationId)?.sandbox;
  if (!sandbox) return null;
  return (
    <div className="box card p-3 mb-3">
      <h2 className="h6">Ambiente de testes</h2>
      <p className="small text-muted">Simule um evento de rastreio do {providerLabel(shipment.provider)}.</p>
      <div className="d-flex flex-wrap gap-2">
        {SANDBOX_TRACKING_EVENTS.map((e) => (
          <button
            key={e}
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={pending}
            onClick={() => onEvent(e)}
          >
            {SANDBOX_EVENT_LABEL[e]}
          </button>
        ))}
      </div>
    </div>
  );
}
